import { BN } from "@project-serum/anchor";
import { getTokenAccount } from "@saberhq/token-utils";
import { PublicKey } from "@solana/web3.js";
import { SCALED_ONE } from "./constants";
import { getInterest, getLateFee, getLateInterest } from "./utils";
class AccountWrapper {
    constructor(client, accountTypeName, address, _data) {
        this.client = client;
        this.accountTypeName = accountTypeName;
        this.address = address;
        this._data = _data;
    }
    get data() {
        return this._data;
    }
    async reload() {
        const accountData = await this.client.program.account[this.accountTypeName].fetch(this.address);
        // This cast won't be necessary once Anchor's typing is fixed.
        // See https://github.com/project-serum/anchor/issues/1312.
        this._data = accountData;
    }
}
export class WrappedGlobals extends AccountWrapper {
    static async load(client, address) {
        if (!(address instanceof PublicKey)) {
            throw new Error("Address must be a PublicKey");
        }
        const account = new WrappedGlobals(client, "globals", address);
        await account.reload();
        return account;
    }
}
export class WrappedPool extends AccountWrapper {
    static async load(client, address) {
        if (!(address instanceof PublicKey)) {
            throw new Error("Address must be a PublicKey");
        }
        const account = new WrappedPool(client, "pool", address);
        await account.reload();
        return account;
    }
    async getWhitelistedLPsAddr() {
        const lenders = await this.client.program.account.lender.all([
            {
                // Filter by pool field.
                memcmp: {
                    offset: WrappedLender.POOL_FIELD_START_OFFSET,
                    bytes: this.address.toBase58(),
                },
            },
            {
                // Filter by allowlisted field.
                memcmp: {
                    offset: WrappedLender.ALLOWLISTED_FIELD_START_OFFSET,
                    bytes: "2", // base58 encoding for true or 1.
                },
            },
        ]);
        return lenders.map((lender) => lender.account.owner.toBase58());
    }
    computeAssetForShares(shares) {
        if (this.data.sharesOutstanding.isZero()) {
            return new BN(0);
        }
        return shares.mul(this.data.totalValue).div(this.data.sharesOutstanding);
    }
    computeSharesForAsset(asset) {
        const { totalValue, sharesOutstanding } = this.data;
        if (totalValue.isZero() && sharesOutstanding.isZero()) {
            return asset;
        }
        if (totalValue.isZero() || sharesOutstanding.isZero()) {
            throw new Error("Invalid Pool");
        }
        else {
            return asset.mul(sharesOutstanding).div(totalValue);
        }
    }
}
export class WrappedLender extends AccountWrapper {
    static async load(client, address) {
        if (!(address instanceof PublicKey)) {
            throw new Error("Address must be a PublicKey");
        }
        const account = new WrappedLender(client, "lender", address);
        await account.reload();
        return account;
    }
    async getLockedShares() {
        const account = await getTokenAccount(this.client.provider, this.data.lockedShares);
        return account.amount;
    }
    async getUnlockedShares() {
        const account = await getTokenAccount(this.client.provider, this.data.lenderShares);
        return account.amount;
    }
    async getTotalShares() {
        return (await this.getLockedShares()).add(await this.getUnlockedShares());
    }
}
// The offset from a lender account of the end of the struct discriminator
WrappedLender.POOL_FIELD_START_OFFSET = 8;
// The offset from a lender account of the beginning of allowlisted field
// discriminator_size + pool.PublicKey + owner.PublicKey.
WrappedLender.ALLOWLISTED_FIELD_START_OFFSET = 72;
export class WrappedWithdrawalRequest extends AccountWrapper {
    static async load(client, address) {
        if (!(address instanceof PublicKey)) {
            throw new Error("Address must be a PublicKey");
        }
        const account = new WrappedWithdrawalRequest(client, "withdrawalRequest", address);
        await account.reload();
        return account;
    }
}
export class WrappedLoan extends AccountWrapper {
    static async load(client, address) {
        if (!(address instanceof PublicKey)) {
            throw new Error("Address must be a PublicKey");
        }
        const account = new WrappedLoan(client, "loan", address);
        await account.reload();
        return account;
    }
    async getLoanAmount() {
        const account = await getTokenAccount(this.client.provider, this.data.locker);
        return account.amount;
    }
}
WrappedLoan.GLOBALS_FIELD_START_OFFSET = 8;
// discriminator_size (8) + globals.PublicKey (32) + borrower.PublicKey (32).
WrappedLoan.POOL_FIELD_START_OFFSET = 72;
export class WrappedOpenTermLoan extends AccountWrapper {
    static async load(client, address) {
        if (!(address instanceof PublicKey)) {
            throw new Error("Address must be a PublicKey");
        }
        const account = new WrappedOpenTermLoan(client, "openTermLoan", address);
        await account.reload();
        return account;
    }
    async getLoanAmount() {
        const account = await getTokenAccount(this.client.provider, this.data.locker);
        return account.amount;
    }
    getPaymentBreakdown(now) {
        const { nextPaymentTs } = this.data;
        const { paymentInterval, lateInterestRate, lateFeeRate, interestRate, principalRequested, } = this.data.config;
        return {
            regularInterest: getInterest(principalRequested, interestRate, paymentInterval),
            lateInterest: getLateInterest(now, principalRequested, nextPaymentTs, lateInterestRate),
            lateFee: getLateFee(now, nextPaymentTs, lateFeeRate, principalRequested),
        };
    }
    getRepaymentBreakdown(now) {
        const { nextPaymentTs } = this.data;
        const { principalRequested, interestRate, paymentInterval, lateFeeRate, lateInterestRate, } = this.data.config;
        const prevPaymentTs = nextPaymentTs.sub(paymentInterval);
        let regularInterestInterval = new BN(0);
        if (prevPaymentTs.lt(now)) {
            regularInterestInterval = BN.min(now.sub(prevPaymentTs), paymentInterval);
        }
        return {
            regularInterest: getInterest(principalRequested, interestRate, regularInterestInterval),
            lateInterest: getLateInterest(now, principalRequested, nextPaymentTs, lateInterestRate),
            lateFee: getLateFee(now, nextPaymentTs, lateFeeRate, principalRequested),
            principal: principalRequested,
        };
    }
    getOngoingFees(delegateDrawdownFeeRate, treasuryDrawdownFeeRate, interval) {
        if (interval.eqn(0)) {
            return {
                delegateOngoingFee: new BN(0),
                treasuryOngoingFee: new BN(0),
            };
        }
        const { principalRequested } = this.data.config;
        return {
            delegateOngoingFee: getInterest(principalRequested, delegateDrawdownFeeRate, interval),
            treasuryOngoingFee: getInterest(principalRequested, treasuryDrawdownFeeRate, interval),
        };
    }
    getInterestFees(interest, delegateRepaymentFeeRate, treasuryRepaymentFeeRate) {
        return {
            delegateRepaymentFee: interest
                .mul(delegateRepaymentFeeRate)
                .div(SCALED_ONE),
            treasuryRepaymentFee: interest
                .mul(treasuryRepaymentFeeRate)
                .div(SCALED_ONE),
        };
    }
    getProRataOngoingFees(now, delegateOngoingFeeRate, treasuryOngoingFeeRate) {
        const previousPaymentTs = this.data.nextPaymentTs.sub(this.data.config.paymentInterval);
        if (previousPaymentTs.lt(now)) {
            return this.getOngoingFees(delegateOngoingFeeRate, treasuryOngoingFeeRate, now.sub(previousPaymentTs));
        }
        return {
            delegateOngoingFee: new BN(0),
            treasuryOngoingFee: new BN(0),
        };
    }
    isRefinanceable(now) {
        const state = Object.keys(this.data.state)[0];
        return ((state === "active" || state === "called") &&
            this.data.proposeRefinanceTs.add(this.data.config.refinancePeriod).gt(now));
    }
}
WrappedOpenTermLoan.GLOBALS_FIELD_START_OFFSET = 8;
//# sourceMappingURL=wrapped_accounts.js.map